import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaEye, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { projects } from '../data/portfolioData';
import AnimatedSection from './AnimatedSection';
import { useTranslation } from 'react-i18next';
import { localizedItem } from '../locales/contentTranslations';
import useCardSlider from "../hooks/useCardSlider";

const ProjectsSlider = () => {
  const { t, i18n } = useTranslation();
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [filter, setFilter] = useState('all');

  const localizedProjects = projects.map(item => localizedItem('projects', item, i18n));
  const selectedProject = localizedProjects.find(p => p.id === selectedProjectId);
  const filteredProjects = filter === 'all'
    ? localizedProjects
    : localizedProjects.filter(p => p.featured);

  
  const {
    startIndex,
    setStartIndex,
    visibleCount,
    next: nextSlide,
    prev: prevSlide,
    reset,
    sliderProps
  } = useCardSlider(
    filteredProjects.length,
    selectedProjectId !== null,
    10000,
    true
  );

  const totalPages = Math.ceil(filteredProjects.length / visibleCount);
  const currentPage = Math.floor(startIndex / visibleCount);

  const visibleProjects = filteredProjects.slice(
    startIndex,
    startIndex + visibleCount
  );


  const handleImageError = (projectId) => {
    setImageErrors(prev => ({ ...prev, [projectId]: true }));
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    reset();
  };

  return (
    <section id="projects" className="py-32 relative overflow-hidden">
      {/* Animirana pozadina */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/3 -left-48 w-96 h-96 bg-primary rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/3 -right-48 w-96 h-96 bg-secondary rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <AnimatedSection>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            {t("projects.title")}
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            {t("projects.subtitle")}
          </p>
        </AnimatedSection>

        {/* Filter buttons */}
        <div className="flex justify-center gap-4 mb-12">
          {[
            { id: 'all', label: t('projects.allProjects'), icon: '🎯' },
            { id: 'featured', label: t('projects.featured'), icon: '⭐' }
          ].map((f) => (
            <motion.button
              key={f.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleFilterChange(f.id)}
              className={`px-6 py-2 rounded-full transition-all flex items-center gap-2 ${
                filter === f.id 
                  ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/25' 
                  : 'glass-effect text-gray-300 hover:text-white'
              }`}
            >
              <span>{f.icon}</span>
              <span>{f.label}</span>
            </motion.button>
          ))}
        </div>

        {/* Slajder */}
        <div className="relative" {...sliderProps}>
          {/* Navigation dugmad */}
          {filteredProjects.length > visibleCount && (
            <>
              <button
                onClick={prevSlide}
                className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition hover-glow"
              >
                <FaChevronLeft size={20} />
              </button>
              
              <button
                onClick={nextSlide}
                className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition hover-glow"
              >
                <FaChevronRight size={20} />
              </button>
            </>
          )}

          {/* Projects grid */}
          <div>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-sm mx-auto md:max-w-none">
             <AnimatePresence mode="wait">
              {visibleProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="group relative min-w-0 h-full"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                  <div className="relative glass-effect rounded-2xl overflow-hidden cursor-pointer h-full flex flex-col">
                    {/* Project image */}
                    <div 
                      className="relative h-56 overflow-hidden bg-gradient-to-br from-primary/20 to-secondary/20"
                      onClick={() => setSelectedProjectId(project.id)}
                    >
                      {!imageErrors[project.id] && project.imageUrl ? (
                        <>
                          <img
                            src={project.imageUrl}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={() => handleImageError(project.id)}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-dark via-transparent to-transparent opacity-60"></div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-7xl">
                          {project.image}
                        </div>
                      )}
                      
                      {/* Overlay sa dugmetom za preview */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="px-6 py-2 bg-white/20 backdrop-blur-md rounded-full text-white font-semibold flex items-center gap-2"
                          onClick={() => setSelectedProjectId(project.id)}
                        >
                          <FaEye /> {t('projects.preview')}
                        </motion.button>
                      </div>
                    </div>
                    
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="text-xl md:text-2xl font-bold mb-2 group-hover:text-primary transition line-clamp-1">
                        {project.title}
                      </h3>
                      <p className="text-gray-400 mb-4 text-sm line-clamp-2">
                        {project.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.tech.slice(0, 3).map((tech, i) => (
                          <span key={i} className="text-xs px-2 py-1 glass-effect rounded-full">
                            {tech}
                          </span>
                        ))}
                        {project.tech.length > 3 && (
                          <span className="text-xs px-2 py-1 glass-effect rounded-full">
                            +{project.tech.length - 3}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-4 mt-auto">
                        {project.demo && (
                          <motion.a
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            href={project.demo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center px-4 py-2 bg-gradient-to-r from-primary to-secondary rounded-lg text-white text-sm font-semibold hover-glow transition"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t('projects.liveDemo')} <FaExternalLinkAlt className="inline ml-1 text-xs" />
                          </motion.a>
                        )}
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-4 py-2 glass-effect rounded-lg text-gray-300 hover:text-white text-sm font-semibold transition"
                          onClick={() => setSelectedProjectId(project.id)}
                        >
                          {t('projects.details')}
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
             </AnimatePresence>
            </div>
          </div>

          {/* Paginacija / Indikatori */}
          {filteredProjects.length > visibleCount && (
            <>
              <div className="flex justify-center gap-1 mt-3">
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setStartIndex(idx * visibleCount)}
                    className={`transition-all ${
                      currentPage === idx 
                        ? 'w-3 h-0.5 bg-primary rounded-full' 
                        : 'w-1 h-0.5 bg-gray-600/40 rounded-full'
                    }`}
                  />
                ))}
              </div>

              <div className="text-center mt-3 sm:mt-4">
                <p className="text-gray-500 text-xs sm:text-sm">
                  {t('projects.showing')} {startIndex + 1} - {Math.min(startIndex + visibleCount, filteredProjects.length)} {t('projects.of')} {filteredProjects.length} {t('projects.items')}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal za detalje projekta - isti kao prije */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-lg z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedProjectId(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              className="relative bg-gradient-to-br from-darker to-dark rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedProjectId(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
              >
                <FaTimes />
              </button>

              <div className="relative h-64 overflow-hidden rounded-t-2xl">
                {!imageErrors[selectedProject.id] && selectedProject.imageUrl ? (
                  <img
                    src={selectedProject.imageUrl}
                    alt={selectedProject.title}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(selectedProject.id)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-8xl bg-gradient-to-br from-primary/20 to-secondary/20">
                    {selectedProject.image}
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-darker via-transparent to-transparent"></div>
              </div>

              <div className="p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-bold mb-2">{selectedProject.title}</h3>
                <p className="text-gray-300 leading-relaxed mb-6">{selectedProject.longDescription}</p>
                
                {/* Video Demo Sekcija */}
                {selectedProject.demoVideo && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                      <span>🎥</span> {t('projects.demoVideo')}
                    </h4>
                    <div className="relative rounded-xl overflow-hidden bg-black">
                      {selectedProject.demoVideo.endsWith('.mp4') ? (
                        <video controls className="w-full h-auto max-h-[400px]">
                          <source src={selectedProject.demoVideo} type="video/mp4" />
                        </video>
                      ) : (
                        <div className="aspect-video">
                          <iframe
                            src={selectedProject.demoVideo}
                            className="w-full h-full"
                            frameBorder="0"
                            allowFullScreen
                            title={`Demo za ${selectedProject.title}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                    <span>✨</span> {t('projects.keyFeatures')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {selectedProject.features.map((feature, i) => (
                      <span key={i} className="px-3 py-2 glass-effect rounded-lg text-sm flex items-center gap-2">
                        <span className="text-primary">▹</span> {feature}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="mb-8">
                  <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                    <span>⚙️</span> {t('projects.technologies')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tech.map((tech, i) => (
                      <span key={i} className="px-3 py-1.5 bg-primary/20 rounded-full text-sm font-medium">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-4">
                  {selectedProject.demo && (
                    <a
                      href={selectedProject.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center py-3 bg-gradient-to-r from-primary to-secondary rounded-xl font-semibold hover-glow transition flex items-center justify-center gap-2"
                    >
                      <FaExternalLinkAlt /> {t('projects.visitLiveDemo')}
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProjectsSlider;